import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CAccordion from "../../components/CAccordion";
import CButton from "../../components/CButton";
import CCheckbox from "../../components/CCheckbox";
import CTextField from "../../components/CTextField";
import { fromWithCompany, supabase } from "../../config/supabaseClient";
import CFormTextField from "../../components/HookFormComponents/CFormTextField";
import {
  setTotalBudget,
  fetchTotalBudget,
  saveTotalBudget,
} from "../../slices/totalBudgetSlice";
import { formatZAR } from "../../constant/utils/commonUtils";
import { messages } from "../../constant/alertMessages";
import { MAX_DEPARTMENT_BUDGET } from "../../constant/constants";

// Function to convert flat department data to nested structure
const convertToNestedStructure = (flatDepartments) => {
  // Create a map for quick lookup of departments by ID
  const departmentMap = new Map();

  // First pass: Create department objects with empty sub_departments array
  flatDepartments.forEach((dept) => {
    departmentMap.set(dept.dept_id, {
      department_id: dept.dept_id,
      department_name: dept.dept_name,
      parent_department_id: dept.parent_dept_id,
      budget: dept.budget,
      distribute_equally: dept.distribute_equally,
      sub_departments: [],
    });
  });

  // Second pass: Build the tree structure
  const rootDepartments = [];

  flatDepartments.forEach((dept) => {
    const department = departmentMap.get(dept.dept_id);

    if (dept.parent_dept_id === null) {
      // This is a root department
      rootDepartments.push(department);
    } else {
      // This is a child department
      const parent = departmentMap.get(dept.parent_dept_id);
      if (parent) {
        parent.sub_departments.push(department);
      }
    }
  });

  return rootDepartments;
};

const calculateLeafBudget = (department) => {
  if (!department.sub_departments || department.sub_departments.length === 0) {
    return department.budget;
  }
  return department.sub_departments.reduce(
    (total, sub) => total + calculateLeafBudget(sub),
    0
  );
};

const calculateSubDepartmentTotal = (department) => {
  if (!department.sub_departments || department.sub_departments.length === 0) {
    return 0;
  }
  return department.sub_departments.reduce(
    (total, sub) => total + sub.budget + calculateSubDepartmentTotal(sub),
    0
  );
};

const DepartmentNode = React.memo(
  ({ department, level = 0, handleBudgetChange, handleDistributeEqually }) => {
    const hasSubDepartments =
      department.sub_departments && department.sub_departments.length > 0;
    const showDistributeEqually =
      hasSubDepartments && department.sub_departments.length > 1;

    // Calculate total child budget
    const totalChildBudget = hasSubDepartments
      ? calculateSubDepartmentTotal(department)
      : 0;

    return (
      <li className="relative mb-4">
        <div className="flex items-center mb-2 relative">
          <span className="absolute -left-8 top-[50%] w-6 h-px bg-blue-200 after:content-[''] after:absolute after:right-0 after:w-[6px] after:h-[6px] after:rotate-45 after:top-[-2.5px] after:bg-blue-200"></span>
          <span className="font-semibold text-gray-700 2xl:mr-4 mr-2 2xl:text-base 2xl:leading-base text-sm leading-sm">
            {department.department_name}
          </span>

          <div className="flex items-center gap-4 accordion-steppers-fields">
            <div className="relative">
              <span className="absolute left-2 top-[calc(50%-12px)] z-[10]">
                R
              </span>
              <CTextField
                value={department.budget}
                onChange={(e) =>
                  handleBudgetChange(
                    department.department_id,
                    e.target.value,
                    department.parent_department_id
                  )
                }
                className="border border-blue-200 rounded-lg px-4 py-2 flex-1 max-w-fit"
                placeholder=""
              />
            </div>

            {department.sub_departments &&
              department.sub_departments.length > 0 && (
                // <CCheckbox
                //   label="Distribute equally"
                //   checked={department.distribute_equally}
                //   onChange={(e) =>
                //     handleDistributeEqually(
                //       department.department_id,
                //       e.target.checked
                //     )
                //   }
                //   name={`distribute-${department.department_id}`}
                // />
                <div className="flex gap-1 items-center">
                  <label>Sub-department Total:</label>
                  {formatZAR(totalChildBudget)}
                </div>
              )}
          </div>
        </div>

        {hasSubDepartments && (
          <ul className="relative pl-8 ml-3 before:content-[''] before:absolute before:left-0 before:top-0 before:w-px before:h-[calc(100%-25px)] before:bg-[#aae3ff]">
            {department.sub_departments.map((subDept) => (
              <DepartmentNode
                key={subDept.department_id}
                department={subDept}
                level={level + 1}
                handleBudgetChange={handleBudgetChange}
                handleDistributeEqually={handleDistributeEqually}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }
);

function BudgetAllocation() {
  const dispatch = useDispatch();
  const { totalBudget } = useSelector((state) => state.totalBudget);

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yearId, setYearId] = useState(null);
  const [configurationId, setConfigurationId] = useState(null);
  const [departmentConfig, setDepartmentConfig] = useState([]);
  const [budgetError, setBudgetError] = useState(null);
  // Calculate total budget for child departments only
  const calculateDepartmentTotal = useCallback((dept) => {
    if (!dept.sub_departments?.length) return 0;

    return dept.sub_departments.reduce((total, subDept) => {
      const subDeptTotal = calculateDepartmentTotal(subDept);
      return total + (Number(subDept.budget) || 0) + subDeptTotal;
    }, 0);
  }, []);

  // Modify the calculateTotalBudget function to sum each department's total calculated budget
  const calculateTotalBudget = () => {
    return departments.reduce(
      (sum, dept) => sum + calculateLeafBudget(dept),
      0
    );
  };
  // useEffect(() => {
  //   const totalBudgetValue = parseFloat(totalBudget) || 0;
  //   console.log("totalBudgetValue", totalBudgetValue);
  //   const totalRandValue = departments.reduce(
  //     (sum, item) => sum + (parseFloat(item.budget) || 0),
  //     0
  //   );
  //   console.log("totalRandValue", totalRandValue);
  //   if (totalBudgetValue.toFixed(2) !== totalRandValue.toFixed(2)) {
  //     setBudgetError(messages.MISMATCH_BUDGET(totalBudgetValue, totalRandValue));
  //   } else {
  //     setBudgetError(null);
  //   }
  // }, [departments, totalBudget]);

  useEffect(() => {
    // Calculate the sum of all departments and their sub-departments' budgets
    const getTotalBudgetIncludingSubDepartments = (departments) => {
      const sumBudgets = (dept) => {
        let total = Number(dept.budget) || 0;
        if (dept.sub_departments && dept.sub_departments.length > 0) {
          total += dept.sub_departments.reduce(
            (sum, subDept) => sum + sumBudgets(subDept),
            0
          );
        }
        return total;
      };
      return departments.reduce((sum, dept) => sum + sumBudgets(dept), 0);
    };

    // Update the total budget in the redux store whenever departments or totalBudget changes
    const calculatedTotal = getTotalBudgetIncludingSubDepartments(departments);
    if (!isNaN(calculatedTotal)) {
      dispatch(setTotalBudget(calculatedTotal));
    }
  }, [departments, totalBudget]);

  useEffect(() => {
    fetchYearId();
  }, []);

  useEffect(() => {
    if (yearId) {
      fetchConfiguration();
      // Fetch total budget from database
      const companyId = JSON.parse(localStorage.getItem("sdfUser"))?.company_id;
      if (companyId) {
        dispatch(fetchTotalBudget({ yearId, companyId }));
      }
    }
  }, [yearId, dispatch]);

  const fetchYearId = async () => {
    try {
      const { data, error } = await fromWithCompany("year")
        .select("id")
        .eq("status", true)
        .lte("start_date", new Date().toISOString())
        .gte("end_date", new Date().toISOString())
        .single();

      if (error) {
        console.error("Error fetching year_id:", error);
        return;
      }

      if (data) {
        setYearId(data.id);
      }
    } catch (error) {
      console.error("Error in fetchYearId:", error);
    }
  };

  const fetchConfiguration = async () => {
    try {
      const { data, error } = await fromWithCompany("configurations")
        .select("id, department_config")
        .eq("year_id", yearId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching configuration:", error);
        await fetchDepartments([]);
        return;
      }

      if (data) {
        setConfigurationId(data.id);
        setDepartmentConfig(data.department_config || []);
        await fetchDepartments(data.department_config || []);
      } else {
        // No configuration row yet — still load organogram departments
        await fetchDepartments([]);
      }
    } catch (error) {
      console.error("Error in fetchConfiguration:", error);
      await fetchDepartments([]);
    }
  };

  const fetchDepartments = async (savedConfig = []) => {
    try {
      setLoading(true);
      const { data, error } = await fromWithCompany("organograms")
        .select("dept_id, dept_name, parent_dept_id")
        .eq("status", "active");

      if (error) {
        throw error;
      }

      if (data) {
        // Apply saved configuration to departments before converting to nested structure
        const departmentsWithConfig = data.map((dept) => {
          const deptConfig = savedConfig.find(
            (c) => c.dept_id === dept.dept_id
          );
          return {
            ...dept,
            budget: deptConfig?.budget ?? 0,
            distribute_equally: deptConfig?.distribute_equally ?? false,
          };
        });

        const nestedDepartments = convertToNestedStructure(
          departmentsWithConfig
        );
        setDepartments(nestedDepartments);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBudgetChange = (departmentId, value, parentId = null) => {
    const budgetValue = Number(value) || 0;

    // Block input if value exceeds 100 million
    if (budgetValue > MAX_DEPARTMENT_BUDGET) {
      return; // Don't update the state, effectively blocking the input
    }

    setDepartments((prevDepts) => {
      const updateDepartment = (depts) => {
        return depts.map((dept) => {
          if (dept.department_id === departmentId) {
            return {
              ...dept,
              budget: budgetValue,
            };
          }
          if (dept.sub_departments) {
            return {
              ...dept,
              sub_departments: updateDepartment(dept.sub_departments),
            };
          }

          if (parentId && dept.department_id === parentId) {
            const totalSubBudget =
              dept.sub_departments?.reduce(
                (sum, subDept) => sum + (Number(subDept.budget) || 0),
                0
              ) || 0;
            return {
              ...dept,
              budget: totalSubBudget,
            };
          }
          return dept;
        });
      };
      return updateDepartment(prevDepts);
    });
  };

  const handleDistributeEqually = (departmentId, checked) => {
    setDepartments((prevDepts) => {
      const updateDepartment = (depts) => {
        return depts.map((dept) => {
          if (dept.department_id === departmentId) {
            const updatedDept = {
              ...dept,
              distribute_equally: checked,
            };

            if (checked && dept.sub_departments) {
              const equalBudget = dept.budget / dept.sub_departments.length;
              updatedDept.sub_departments = dept.sub_departments.map(
                (subDept) => ({
                  ...subDept,
                  budget: equalBudget,
                })
              );
            }

            return updatedDept;
          }
          if (dept.sub_departments) {
            return {
              ...dept,
              sub_departments: updateDepartment(dept.sub_departments),
            };
          }
          return dept;
        });
      };
      return updateDepartment(prevDepts);
    });
  };

  const flattenSubtree = (dept) => {
    let result = [
      {
        dept_id: dept.department_id,
        budget: dept.budget,
        distribute_equally: dept.distribute_equally,
      },
    ];
    if (dept.sub_departments && dept.sub_departments.length > 0) {
      dept.sub_departments.forEach((subDept) => {
        result = result.concat(flattenSubtree(subDept));
      });
    }
    return result;
  };

  const handleSave = async (parentDepartment) => {
    try {
      // Create config array for the current department and its sub-departments
      const createConfigArray = (dept) => {
        const config = [
          {
            dept_id: dept.department_id,
            budget: dept.budget,
            distribute_equally: dept.distribute_equally,
          },
        ];

        if (dept.sub_departments) {
          dept.sub_departments.forEach((subDept) => {
            config.push({
              dept_id: subDept.department_id,
              budget: subDept.budget,
              distribute_equally: subDept.distribute_equally,
            });
          });
        }

        return config;
      };

      // const totalBudgetValue = parseFloat(totalBudget) || 0;
      //       const totalRandValue = departments.reduce((sum, item) => sum + (parseFloat(item.budget) || 0), 0);

      // if (totalBudgetValue.toFixed(2) !== totalRandValue.toFixed(2)) { // Allow small rounding differences
      //   toast.error(messages.MISMATCH_BUDGET(totalRandValue.toFixed(2),totalBudgetValue.toFixed(2)));
      //   return;
      // }

      const newDepartmentConfig = flattenSubtree(parentDepartment);

      // Get all department IDs that are being updated
      const updatedDeptIds = newDepartmentConfig.map(
        (config) => config.dept_id
      );

      // Filter out the departments being updated from the current config
      const otherDepartmentsConfig = departmentConfig.filter(
        (config) => !updatedDeptIds.includes(config.dept_id)
      );

      // Combine the other departments' config with the new department's config
      const updatedConfig = [...otherDepartmentsConfig, ...newDepartmentConfig];

      // Calculate sum of all department budgets
      const departmentTotal = updatedConfig.reduce(
        (sum, dept) => sum + (parseFloat(dept.budget) || 0),
        0
      );

      // Update the configuration with department total budget
      const { error: updateError } = await supabase
        .from("configurations")
        .update({
          department_config: updatedConfig,
          total_budget: departmentTotal, // Use calculated department total
        })
        .eq("id", configurationId)
        .select();

      if (updateError) {
        toast.error(
          updateError?.message || "Failed to save budget configuration"
        );
        return;
      }
      // Update local state with new configuration
      setDepartmentConfig(updatedConfig);
      toast.success(
        `Budget configuration saved successfully for ${parentDepartment.department_name}!`
      );
    } catch (error) {
      console.error("Error saving configuration:", error);
      toast.error("Failed to save budget configuration");
    }
  };

  const handleCancel = (department) => {
    setDepartments((prevDepts) => {
      const resetDepartment = (depts) => {
        return depts.map((dept) => {
          if (dept.department_id === department.department_id) {
            // Reset this department and its sub-departments
            const resetSubDepartments = (subDepts) => {
              return subDepts.map((subDept) => {
                const originalConfig = departmentConfig.find(
                  (config) => config.dept_id === subDept.department_id
                );

                return {
                  ...subDept,
                  budget: originalConfig?.budget ?? 0,
                  distribute_equally:
                    originalConfig?.distribute_equally ?? false,
                  sub_departments: subDept.sub_departments
                    ? resetSubDepartments(subDept.sub_departments)
                    : [],
                };
              });
            };

            const originalConfig = departmentConfig.find(
              (config) => config.dept_id === dept.department_id
            );

            return {
              ...dept,
              budget: originalConfig?.budget ?? 0,
              distribute_equally: originalConfig?.distribute_equally ?? false,
              sub_departments: dept.sub_departments
                ? resetSubDepartments(dept.sub_departments)
                : [],
            };
          }
          if (dept.sub_departments) {
            return {
              ...dept,
              sub_departments: resetDepartment(dept.sub_departments),
            };
          }
          return dept;
        });
      };
      return resetDepartment(prevDepts);
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading departments...
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 p-4">Error: {error}</div>;
  }

  if (!departments.length) {
    return (
      <div className="flex justify-center items-center h-screen">
        <span className="text-gray-500 text-lg font-semibold">
          No data available! To view the data, please upload organogram.
        </span>
      </div>
    );
  }

  /**
   * This function checks recursively if all departments (including sub-departments) have a budget of zero.
   * It returns true only if the current department and all its sub-departments have a budget of 0.
   */
  const checkIfAllDepartmentsHaveZeroBudget = (department) => {
    // Check if current department has zero budget
    if (department.budget !== 0) {
      return false;
    }

    // If no sub-departments, return true (current department is 0)
    if (
      !department.sub_departments ||
      department.sub_departments.length === 0
    ) {
      return true;
    }

    // Recursively check all sub-departments
    return department.sub_departments.every((subDept) =>
      checkIfAllDepartmentsHaveZeroBudget(subDept)
    );
  };

  return (
    <>
      <div className="relative py-[1px]">
        {budgetError && (
          <p className="text-red-500 text-sm leading-sm absolute top-[-12px] w-full text-center">
            {budgetError}
          </p>
        )}
      </div>
      <div className="bg-white rounded-lg p-6 border border-blue-200 relative mt-4 flex flex-col gap-4">
        {/* <p className="text-gray-900 font-semibold 2xl:text-2xl 2xl:leading-2xl text-xl leading-xl mb-1 absolute top-[-14px] left-6 bg-white px-2"></p> */}

        {departments.map((department) => (
          <CAccordion
            key={department.department_id}
            expanded={true}
            title={
              <div className="flex items-center relative">
                <span className="font-semibold text-gray-700 2xl:mr-4 mr-2 2xl:text-base 2xl:leading-base text-sm leading-sm">
                  {department.department_name}
                </span>
                <div className="flex items-center gap-4 accordion-steppers-fields">
                  <div className="relative">
                    <span className="absolute left-2 top-[calc(50%-12px)] z-[10]">
                      R
                    </span>
                    <CTextField
                      value={department.budget}
                      onChange={(e) =>
                        handleBudgetChange(
                          department.department_id,
                          e.target.value,
                          department.parent_department_id
                        )
                      }
                      className="border border-blue-200 rounded-lg px-4 py-2 flex-1 max-w-fit"
                      placeholder=""
                    />
                  </div>
                  {department.sub_departments &&
                    department.sub_departments.length > 0 && (
                      // <CCheckbox
                      //   label="Distribute equally"
                      //   checked={department.distribute_equally}
                      //   onChange={(e) =>
                      //     handleDistributeEqually(
                      //       department.department_id,
                      //       e.target.checked
                      //     )
                      //   }
                      //   name={`distribute-${department.department_id}`}
                      // />
                      <div className="flex gap-1 items-center">
                        <label>Sub-department Total:</label>
                        {formatZAR(calculateSubDepartmentTotal(department))}
                      </div>
                    )}
                </div>
              </div>
            }
          >
            <div className="flex flex-col gap-4">
              <ul className="relative w-full overflows-auto pl-8 ml-8 before:content-[''] before:absolute before:left-0 before:top-0 before:w-px before:h-[calc(100%-25px)] before:bg-[#aae3ff]">
                {department.sub_departments?.map((subDept) => (
                  <DepartmentNode
                    key={subDept.department_id}
                    department={subDept}
                    handleBudgetChange={handleBudgetChange}
                    handleDistributeEqually={handleDistributeEqually}
                  />
                ))}
              </ul>
              <div className="flex justify-end gap-4 mt-4">
                <CButton
                  onClick={() => handleSave(department)}
                  className="bg-blue-500 text-white border border-blue-500 hover:bg-blue-700 hover:border-blue-700 min-w-[100px]"
                  disabled={checkIfAllDepartmentsHaveZeroBudget(department)}
                >
                  Save Configuration
                </CButton>
              </div>
            </div>
          </CAccordion>
        ))}
      </div>
    </>
  );
}

export default BudgetAllocation;
